/**
 * @packageDocumentation
 * @module f1/fn
 */

export interface Memoize {
  /**
   * Wrap a function and track inputs so we only calculate each parameter set once. This is important
   * if your function is either:
   *
   * - expensive
   * - used in a recursive function
   *
   * @public
   * @function f1.memoize
   * @param fn the function to memoize
   * @example
   *
   * ```ts
   * const plus = f1.memoize((v) => v + 1);
   *
   * plus(1) // add to cache
   * plus(2) // add to cache
   * plus(1) // use cache
   * ```
   */
  <V, FN extends Function>(fn: FN): FN;
}

/**
 * @see Memoize
 */
export default <V, FN extends Function>(fn: FN): FN => {
  const cache = new WeakMap();

  function f(...args: unknown[]): V {
    if (!cache.has(args)) {
      cache.set(args, (fn as FN)(...args));
    }

    return cache.get(args);
  }

  return (f as unknown) as FN;
};
